/**
 * Local mirror of the PostHog Conversations shapes we use. Mirrored rather
 * than imported so consumers of @cosmos/angular/utils don't need posthog-js
 * types on their path. Keep in sync with node_modules/posthog-js/dist/conversations.d.ts.
 */

export type MessageAuthorType = 'customer' | 'AI' | 'human';

export type TicketStatus = 'new' | 'open' | 'pending' | 'on_hold' | 'resolved';

export interface SupportMessage {
  id: string;
  content: string;
  author_type: MessageAuthorType;
  author_name?: string;
  created_at: string;
  is_private: boolean;
}

export interface SupportTicket {
  id: string;
  /** Human-facing sequential number assigned by PostHog (e.g. "Ticket #42"). */
  ticket_number?: number;
  status: TicketStatus;
  last_message?: string;
  last_message_at?: string;
  message_count: number;
  created_at: string;
  unread_count?: number;
}

export type OptimisticStatus = 'pending' | 'sent' | 'failed';

/** A message shown in the thread that may not yet be confirmed by the server. */
export interface OptimisticMessage extends SupportMessage {
  localId: string;
  optimisticStatus: OptimisticStatus;
}

export type PollMode = 'thread' | 'list' | 'background' | 'paused';

/** PrimeNG p-tag severity for a ticket status. Single source of truth for the
 *  status colour, used by both the ticket-list row and the thread-view header. */
export type TicketStatusSeverity =
  | 'info'
  | 'secondary'
  | 'warn'
  | 'danger'
  | 'success';

export function ticketStatusSeverity(
  status: TicketStatus,
): TicketStatusSeverity {
  switch (status) {
    case 'new':
      return 'info';
    case 'open':
      return 'secondary';
    case 'pending':
      return 'warn';
    case 'on_hold':
      return 'danger';
    case 'resolved':
      return 'success';
  }
}

/** i18n key for a ticket status label (handles the on_hold → onHold casing). */
export function ticketStatusKey(status: TicketStatus): string {
  return status === 'on_hold'
    ? 'support.chat.status.onHold'
    : `support.chat.status.${status}`;
}
